// EmailSettings.jsx
import React, { useState, useEffect, useContext } from 'react';
import { UserContext } from '../../Context/UserContext';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaEnvelope, FaServer, FaUserLock, FaCog, FaList, FaTrash, FaEdit } from 'react-icons/fa';
import axios from 'axios';
import { checkIsAdmin, checkIsGlobalAdmin } from '../../utils/authUtils';
import "./CreateItemPage.css";
import CustomerCustomizationModal from "./CustomerCustomizationModal";
import { FaCogs } from 'react-icons/fa';

// Move InputField outside to prevent focus loss on re-render
const InputField = ({ label, value, onChange, placeholder, type = "text", required = false, description = "" }) => (
  <div style={{ marginBottom: '20px' }}>
    <label style={{
      display: 'block',
      marginBottom: '8px',
      fontWeight: '600',
      fontSize: '0.9rem',
      color: '#2c3e50',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    }}>
      {label} {required && <span style={{ color: '#e74c3c' }}>*</span>}
    </label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{
        width: '100%',
        padding: '12px',
        border: '1px solid #ced4da',
        borderRadius: '8px',
        fontSize: '1rem',
        outline: 'none',
        transition: 'all 0.3s ease',
        background: '#f8f9fa'
      }}
      onFocus={(e) => {
        e.target.style.borderColor = '#3498db';
        e.target.style.boxShadow = '0 0 0 3px rgba(52, 152, 219, 0.1)';
        e.target.style.background = '#fff';
      }}
      onBlur={(e) => {
        e.target.style.borderColor = '#ced4da';
        e.target.style.boxShadow = 'none';
        e.target.style.background = '#f8f9fa';
      }}
    />
    {description && (
      <small style={{ display: 'block', marginTop: '5px', color: '#7f8c8d' }}>
        {description}
      </small>
    )}
  </div>
);

function EmailSettings() {
  const navigate = useNavigate();

  // General State
  const [emailType, setEmailType] = useState('gmail'); // 'gmail' or 'webmail'
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const { baseUrl, getHeaders } = useContext(UserContext);

  const [doctypeFields, setDoctypeFields] = useState([]);
  const [showCustomizeModal, setShowCustomizeModal] = useState(false);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);

  // Gmail Specific State
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fromEmail, setFromEmail] = useState('');

  // Web Mail Specific State
  const [senderName, setSenderName] = useState('');
  const [senderEmail, setSenderEmail] = useState('');
  const [smtpServer, setSmtpServer] = useState('');
  const [smtpPort, setSmtpPort] = useState('');
  const [imapServer, setImapServer] = useState('');
  const [imapPort, setImapPort] = useState('');
  const [username, setUsername] = useState('');
  const [webmailPassword, setWebmailPassword] = useState('');
  
  const [isCompanyAdmin, setIsCompanyAdmin] = useState(false);
  const [isGroupAdmin, setIsGroupAdmin] = useState(false);
  const [assignedCompanies, setAssignedCompanies] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [userBranch, setUserBranch] = useState(null);
  const [savedSettingsList, setSavedSettingsList] = useState([]);

  // Permissions
  const [canRead, setCanRead] = useState(false);
  const [canWrite, setCanWrite] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [canCreate, setCanCreate] = useState(false);
  const [permsLoading, setPermsLoading] = useState(true);
  const [showPermModal, setShowPermModal] = useState(false);
  const [permModalMsg, setPermModalMsg] = useState("");

  const fetchPermissions = async (currentBaseUrl) => {
    try {
      setPermsLoading(true);
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const userObj = JSON.parse(userStr);
        const roleRaw = userObj.role || userObj.UserType || '';
        const branch = userObj.branch_name || userObj.branch || "";

        const isAdminRole = checkIsAdmin(userObj);
        const isGroupAdminRole = checkIsGlobalAdmin(userObj);

        setIsCompanyAdmin(isAdminRole);
        setIsGroupAdmin(isGroupAdminRole);
        setUserBranch(branch);

        if (isGroupAdminRole) {
          const comps = userObj.companies || (userObj.company ? [userObj.company] : []);
          setAssignedCompanies(comps);
        }

        if (roleRaw) {
          const response = await axios.get(`${currentBaseUrl}/api/role-permissions?role=${encodeURIComponent(roleRaw)}`);
          const perms = response.data.permissions || [];
          const pagePerm = perms.find(p => p.pageId === 'email_settings');
          
          if (isAdminRole || isGroupAdminRole) {
            setCanRead(true);
            setCanWrite(true);
            setCanDelete(true);
            setCanCreate(true);
          } else if (pagePerm) {
            setCanRead(pagePerm.canRead === true);
            setCanWrite(pagePerm.canWrite === true);
            setCanDelete(pagePerm.canDelete === true);
            setCanCreate(pagePerm.canCreate === true);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching permissions:", error);
    } finally {
      setPermsLoading(false);
    }
  };

  const [branches, setBranches] = useState([]);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const fetchBranches = async (currentBaseUrl, targetCompany = null) => {
    try {
      const userStr = localStorage.getItem('user');
      const userObj = userStr ? JSON.parse(userStr) : {};
      const company_name = targetCompany || (selectedCompanies.length > 0 ? selectedCompanies.join(',') : "") || userObj.company || "";
      const role = userObj.role?.toLowerCase();
      const branch_name = userObj.branch_name;

      if (!company_name) {
        setBranches([]);
        return;
      }

      const is_Admin = checkIsAdmin(userObj) && (!branch_name || branch_name === 'All Branches' || branch_name === '');


      const response = await axios.get(`${currentBaseUrl || baseUrl}/api/branches`, {
        headers: getHeaders(null, company_name)
      });
      // Handle both object and string responses from branches API
      const processedBranches = (response.data || []).map((b, idx) => {
        if (typeof b === 'string') return { _id: `b-${idx}`, branch_name: b };
        return { 
          _id: b._id || b.id || `b-${idx}`, 
          branch_name: b.branch_name || b.branch || b.name || `Branch ${idx + 1}` 
        };
      });
      setBranches(processedBranches);

      if (!is_Admin && branch_name) {
        setSelectedBranches([branch_name]);
      }
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  };

  const fetchEmailSettings = async (currentBaseUrl, branch = null) => {
    try {
      const userStr = localStorage.getItem('user');
      const userObj = userStr ? JSON.parse(userStr) : {};
      const companyHeader = selectedCompanies.length > 0 ? selectedCompanies.join(',') : (userObj.company || "");
      const branchHeader = branch || (!isCompanyAdmin ? userBranch : null);
      const headers = getHeaders(branchHeader, companyHeader);
      
      if (userStr) {
        const userObj = JSON.parse(userStr);
        const companyHeaders = selectedCompanies.length > 0 ? selectedCompanies.join(',') : (userObj.company || "");
        headers['X-Company-Name'] = companyHeaders;
        const active_branch = branch || (!isCompanyAdmin ? userBranch : null);
        if (active_branch) headers['X-Branch-Name'] = active_branch;
      }

      const response = await axios.get(`${currentBaseUrl || baseUrl}/api/get-email-settings`, { headers });
      if (response.data.success && response.data.settings) {
        const s = response.data.settings;
        const type = s.email_type || 'gmail';
        setEmailType(type);

        if (type === 'gmail') {
          setEmail(s.email || '');
          setFromEmail(s.from_email || '');
          setPassword(s.password || '');
        } else {
          setSenderName(s.sender_name || '');
          setSenderEmail(s.sender_email || '');
          setSmtpServer(s.smtp_server || '');
          setSmtpPort(s.smtp_port ? s.smtp_port.toString() : '587');
          setImapServer(s.imap_server || '');
          setImapPort(s.imap_port ? s.imap_port.toString() : '993');
          setUsername(s.username || '');
          setWebmailPassword(s.password || '');
        }
      } else {
        // Handle success: false (No settings found)
        resetForm();
      }
      // Always fetch the list of existing settings
      fetchSavedSettingsList(currentBaseUrl);
    } catch (error) {
      resetForm();
      // Even on error, try to fetch the list
      fetchSavedSettingsList(currentBaseUrl);
      
      // Don't show error for 404/Not Found as it's a valid state (just no settings yet)
      if (error.response && error.response.status === 404) {
        console.log("No email settings found, ready for new entry.");
      } else if (error.response?.data?.message) {
        console.log(error.response.data.message);
      } else {
        setMessage(`Failed to fetch email settings: ${error.message}`);
        setMessageType('error');
      }
    }
  };

  const fetchDoctypeFields = async (currentBaseUrl) => {
    try {
      const response = await axios.get(`${currentBaseUrl || baseUrl}/api/doctypes/Email`);
      if (response.data && response.data.fields) {
        setDoctypeFields(response.data.fields);
      }
    } catch (error) {
      console.error("Error fetching Email doctype:", error);
    }
  };

  const fetchSavedSettingsList = async (currentBaseUrl) => {
    try {
      const activeUrl = currentBaseUrl || baseUrl;
      const userStr = localStorage.getItem('user');
      const userObj = userStr ? JSON.parse(userStr) : {};
      
      // Ensure we have a company name for the header
      const companyHeader = selectedCompanies.length > 0 
        ? selectedCompanies.join(',') 
        : (userObj.company || "");

      if (!companyHeader) {
        console.warn("fetchSavedSettingsList: No company selected, skipping fetch.");
        setSavedSettingsList([]);
        return;
      }

      const branchHeader = !isCompanyAdmin && userBranch ? userBranch : null;
      const headers = getHeaders(branchHeader, companyHeader);
      
      const response = await axios.get(`${activeUrl}/api/list-email-settings`, { headers });
      setSavedSettingsList(response.data || []);
    } catch (error) {
      console.error("Error listing settings:", error);
      setSavedSettingsList([]);
    }
  };

  const resetForm = () => {
    setEmail('');
    setFromEmail('');
    setPassword('');
    setSenderName('');
    setSenderEmail('');
    setSmtpServer('');
    setSmtpPort('587');
    setImapServer('');
    setImapPort('993');
    setUsername('');
    setWebmailPassword('');
    setEditingId(null);
    if (!isCompanyAdmin && userBranch) {
      setSelectedBranches([userBranch]);
    } else {
      setSelectedBranches([]);
    }
  };

  const fetchConfig = async () => {
    let currentBaseUrl = "";
    try {
      const response = await axios.get("/api/network_info");
      const { config: appConfig } = response.data;
      if (appConfig.mode === "client") {
        currentBaseUrl = `http://${appConfig.server_ip}:6034`;
        setBaseUrl(currentBaseUrl);
      } else {
        setBaseUrl("");
        currentBaseUrl = "";
      }
    } catch (error) {
      console.error("Failed to fetch config:", error);
      setBaseUrl("");
      currentBaseUrl = "";
    } finally {
      fetchPermissions(currentBaseUrl);
      fetchBranches(currentBaseUrl);
      fetchSavedSettingsList(currentBaseUrl);
      fetchDoctypeFields(currentBaseUrl);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  useEffect(() => {
    if (baseUrl) {
      fetchSavedSettingsList(baseUrl);
    }
  }, [selectedCompanies, baseUrl]);

  const handleGoBack = () => {
    navigate('/admin');
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleTestSettings = async () => {
    if (!canWrite) {
      setPermModalMsg("You do not have permission to test email settings.");
      setShowPermModal(true);
      return;
    }

    let payload = { email_type: emailType };
    if (emailType === 'gmail') {
      if (!email || !password) {
        setMessage('Please provide email and app password to test');
        setMessageType('error');
        return;
      }
      payload = { ...payload, email, password };
    } else {
      if (!username || !webmailPassword || !smtpServer || !smtpPort) {
        setMessage('Please provide username, password, SMTP server and port');
        setMessageType('error');
        return;
      }
      payload = { ...payload, username, password: webmailPassword, smtp_server: smtpServer, smtp_port: smtpPort };
    }

    setTesting(true);
    try {
      const userStr = localStorage.getItem('user');
      const userObj = userStr ? JSON.parse(userStr) : {};
      const companyHeader = selectedCompanies.length > 0 ? selectedCompanies.join(',') : userObj.company;
      const branchHeader = selectedBranches.length > 0 ? selectedBranches.join(',') : (!isCompanyAdmin ? userBranch : "");
      const headers = getHeaders(branchHeader, companyHeader);
      
      const response = await axios.post(`${baseUrl}/api/test-email-settings`, payload, { headers });
      setMessage(response.data.message);
      setMessageType('success');
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      setMessage(errorMessage);
      setMessageType('error');
    } finally {
      setTesting(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!canWrite) {
      setPermModalMsg("You do not have permission to save email settings.");
      setShowPermModal(true);
      return;
    }

    let payload = { 
      email_type: emailType,
      branch_names: selectedBranches,
      editingId: editingId
    };

    if (emailType === 'gmail') {
      if (!email || !password || !fromEmail) {
        setMessage('Please provide email, app password, and from email');
        setMessageType('error');
        return;
      }
      if (!validateEmail(email) || !validateEmail(fromEmail)) {
        setMessage('Please enter valid email addresses');
        setMessageType('error');
        return;
      }
      payload = { ...payload, email, password, from_email: fromEmail };
    } else {
      if (!senderEmail || !smtpServer || !smtpPort || !username || !webmailPassword) {
        setMessage('Please fill in all required fields marked with *');
        setMessageType('error');
        return;
      }
      if (!validateEmail(senderEmail)) {
        setMessage('Please enter a valid sender email');
        setMessageType('error');
        return;
      }
      payload = {
        ...payload,
        sender_name: senderName,
        sender_email: senderEmail,
        smtp_server: smtpServer,
        smtp_port: smtpPort,
        imap_server: imapServer,
        imap_port: imapPort,
        username: username,
        password: webmailPassword
      };
    }

    setLoading(true);
    try {
      const userStr = localStorage.getItem('user');
      const userObj = userStr ? JSON.parse(userStr) : {};
      const companyHeader = selectedCompanies.length > 0 ? selectedCompanies.join(',') : userObj.company;
      const branchHeader = selectedBranches.length > 0 ? selectedBranches.join(',') : (!isCompanyAdmin ? userBranch : "");
      const headers = getHeaders(branchHeader, companyHeader);

      const response = await axios.post(`${baseUrl}/api/save-email-settings`, payload, { headers });
      setMessage(response.data.message);
      setMessageType('success');
      resetForm();
      fetchSavedSettingsList(baseUrl);
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      setMessage(errorMessage);
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setEmailType(item.email_type || 'gmail');
    setEditingId(item._id);
    setSelectedBranches(item.branch_names || []);
    if (item.company_name) {
      setSelectedCompanies(item.company_name.split(',').map(c => c.trim()));
    }
    
    if (item.email_type === 'gmail') {
      setEmail(item.email || '');
      setFromEmail(item.from_email || '');
      setPassword(item.password || '');
    } else {
      setSenderName(item.sender_name || '');
      setSenderEmail(item.sender_email || '');
      setSmtpServer(item.smtp_server || '');
      setSmtpPort(item.smtp_port ? item.smtp_port.toString() : '587');
      setImapServer(item.imap_server || '');
      setImapPort(item.imap_port ? item.imap_port.toString() : '993');
      setUsername(item.username || '');
      setWebmailPassword(item.password || '');
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      const userStr = localStorage.getItem('user');
      const userObj = userStr ? JSON.parse(userStr) : {};
      await axios.delete(`${baseUrl}/api/delete-email-settings/${id}`, {
        headers: getHeaders(null, selectedCompanies.length > 0 ? selectedCompanies.join(',') : userObj.company)
      });
      setMessage("Settings deleted successfully");
      setMessageType('success');
      fetchSavedSettingsList(baseUrl);
    } catch (error) {
       setMessage(error.response?.data?.error || "Delete failed");
       setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTarget = (item, targetBranch, targetCompany) => {
    setItemToDelete({ item, targetBranch, targetCompany });
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    const { item, targetBranch, targetCompany } = itemToDelete;
    setShowDeleteConfirm(false);

    const branches = item.branch_names || [];
    const companies = item.company_name ? item.company_name.split(',').map(c => c.trim()) : [];

    // If it's the absolute last target, delete the whole record
    if (branches.length <= 1 && companies.length <= 1) {
      return handleDelete(item._id);
    }

    try {
      setLoading(true);
      
      // Filter out the deleted target
      const newBranches = branches.filter(b => b !== targetBranch);
      const newCompanies = companies.filter(c => c !== targetCompany);

      // If we removed a company, we need to update the X-Company-Name header
      // If we removed a branch, we update branch_names payload
      const payload = {
        ...item,
        branch_names: newBranches,
        editingId: item._id,
        password: item.password // Ensure password is sent for update
      };

      const headers = getHeaders(null, newCompanies.length > 0 ? newCompanies.join(',') : companies.join(','));

      await axios.post(`${baseUrl}/api/save-email-settings`, payload, { headers });
      setMessage(`Removed settings for ${targetBranch || targetCompany}`);
      setMessageType('success');
      fetchSavedSettingsList(baseUrl);
    } catch (error) {
      setMessage(error.response?.data?.error || "Failed to remove target");
      setMessageType('error');
    } finally {
      setLoading(false);
      setItemToDelete(null);
    }
  };

  const toggleBranchSelection = (branch) => {
    setSelectedBranches(prev =>
      prev.includes(branch) ? prev.filter(b => b !== branch) : [...prev, branch]
    );
  };


  if (permsLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: 'linear-gradient(135deg, #ffffff 0%, #3498db 100%)'
      }}>
        <div style={{ textAlign: 'center', color: '#3498db', fontSize: '18px' }}>
          <p>Loading Permissions...</p>
        </div>
      </div>
    );
  }

  if (!canRead && !isCompanyAdmin && !isGroupAdmin && !permsLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: 'linear-gradient(135deg, #ffffff 0%, #3498db 100%)'
      }}>
        <div style={{ textAlign: 'center', color: '#e74c3c', fontSize: '18px', padding: '30px', background: '#fff', borderRadius: '15px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          <h2 style={{ marginBottom: '10px' }}>Access Denied</h2>
          <p>You do not have permission to view Email Settings.</p>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/admin')}
            style={{ marginTop: '20px', padding: '10px 20px', borderRadius: '50px', background: '#3498db', color: '#fff', border: 'none', cursor: 'pointer' }}
          >
            Back to Admin
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="create-item-container main-item-page">
      <style>{`
        .action-btn {
          width: 42px;
          height: 42px;
          border-radius: 10px;
          display: flex;
          justify-content: center;
          align-items: center;
          cursor: pointer;
          transition: all 0.2s ease;
          border: none;
        }
        .action-btn-edit {
          background: rgba(255, 159, 4, 0.15);
          color: #FF9F04;
        }
        .action-btn-edit:hover {
          background: #FF9F04 !important;
          color: #ffffff !important;
          box-shadow: 0 4px 12px rgba(255, 159, 4, 0.3);
        }
        .action-btn-delete {
          background: rgba(255, 89, 94, 0.15);
          color: #FF595E;
        }
        .action-btn-delete:hover {
          background: #FF595E !important;
          color: #ffffff !important;
          box-shadow: 0 4px 12px rgba(255, 89, 94, 0.3);
        }
      `}</style>
      {showPermModal && (
        <div className="warning-modal-overlay">
          <div className="warning-modal">
            <div className="warning-modal-icon" style={{ borderColor: '#fee2e2', color: '#ef4444' }}>⚠️</div>
            <h3>Permission Denied</h3>
            <p>{permModalMsg}</p>
            <div className="modal-actions">
              <button className="modal-btn modal-btn-cancel" onClick={() => setShowPermModal(false)}>Understood</button>
            </div>
          </div>
        </div>
      )}

      {/* Message Popup Modal */}
      {message && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(8px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', animation: 'fadeIn 0.3s ease-out' }}>
          <div style={{ background: '#ffffff', borderRadius: '24px', width: '100%', maxWidth: '420px', padding: '40px 32px', display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: '200px', height: '200px', background: messageType === 'success' ? 'radial-gradient(circle, rgba(6, 214, 160, 0.15) 0%, rgba(255,255,255,0) 70%)' : 'radial-gradient(circle, rgba(255, 159, 4, 0.15) 0%, rgba(255,255,255,0) 70%)', zIndex: 0, pointerEvents: 'none' }}></div>
            <div style={{ width: '84px', height: '84px', borderRadius: '50%', background: messageType === 'success' ? '#ecfdf5' : '#fffbeb', color: messageType === 'success' ? '#06D6A0' : '#FF9F04', border: `2px solid ${messageType === 'success' ? '#a7f3d0' : '#fde68a'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '38px', marginBottom: '24px', boxShadow: messageType === 'success' ? '0 10px 25px -5px rgba(6, 214, 160, 0.2)' : '0 10px 25px -5px rgba(255, 159, 4, 0.2)', zIndex: 1, animation: 'bounceIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)' }}>
              {messageType === 'success' ? '✓' : '!'}
            </div>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '24px', fontWeight: '800', color: '#1e293b', textAlign: 'center', zIndex: 1, fontFamily: `'Gilroy', 'Inter', sans-serif` }}>
              {messageType === 'success' ? "Success" : "Notice"}
            </h3>
            <p style={{ margin: '0 0 32px 0', fontSize: '15px', color: '#64748b', textAlign: 'center', lineHeight: '1.6', zIndex: 1 }}>
              {message}
            </p>
            <div style={{ display: 'flex', gap: '12px', width: '100%', zIndex: 1 }}>
              <button onClick={() => setMessage('')} style={{ flex: 1, height: '48px', background: messageType === 'success' ? '#06D6A0' : '#FF9F04', color: '#ffffff', border: 'none', borderRadius: '14px', fontWeight: '700', fontSize: '15px', cursor: 'pointer', boxShadow: messageType === 'success' ? '0 10px 20px -5px rgba(6, 214, 160, 0.3)' : '0 10px 20px -5px rgba(255, 159, 4, 0.3)', transition: 'all 0.2s ease', fontFamily: `'Gilroy', 'Inter', sans-serif` }}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {showCustomizeModal && (
        <CustomerCustomizationModal
          isOpen={showCustomizeModal}
          onClose={() => setShowCustomizeModal(false)}
          targetDocType="Email"
          onFieldAdded={() => fetchDoctypeFields(baseUrl)}
        />
      )}

      <div className="item-header-section">
        <div className="header-left">
          <button className="header-back-btn" onClick={handleGoBack} disabled={loading || testing}>
            <FaArrowLeft /> Back
          </button>
        </div>
        <div className="header-center">
          <h1>Email Configuration</h1>
        </div>
        <div className="header-actions header-right">
          <button className="header-back-btn" onClick={() => setShowCustomizeModal(true)} style={{ color: '#10b981', borderColor: '#10b981' }}>
            <FaCogs /> Customize
          </button>
          <button className="header-back-btn" onClick={handleTestSettings} disabled={testing || loading}>
            <FaServer /> {testing ? 'Testing...' : 'Test Settings'}
          </button>
          <button className="save-btn" onClick={() => setShowAssignmentModal(true)} disabled={loading || testing}>
            <FaEnvelope /> {loading ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>

      <div className="item-scroll-body">
        <div className="main-content-card">
          
          <div className="form-section-card theme-blue">
            <div className="section-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <div className="section-icon"><FaEnvelope /></div>
                <h2>Email Connection</h2>
              </div>
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label>Email Type <span className="required">*</span></label>
                <select className="input" value={emailType} onChange={(e) => setEmailType(e.target.value)}>
                  <option value="gmail">Gmail</option>
                  <option value="webmail">Web Mail</option>
                </select>
              </div>
            </div>

            <div className="form-grid" style={{ marginTop: '24px' }}>
              {emailType === 'gmail' ? (
                <>
                  <div className="form-group">
                    <label>Email Address <span className="required">*</span></label>
                    <input className="input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="e.g. yourname@gmail.com" />
                  </div>
                  <div className="form-group">
                    <label>From Email Address <span className="required">*</span></label>
                    <input className="input" value={fromEmail} onChange={(e) => setFromEmail(e.target.value)} placeholder="e.g. noreply@restaurant.com" />
                  </div>
                  <div className="form-group">
                    <label>App Password <span className="required">*</span></label>
                    <input className="input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="16-character app password" />
                  </div>
                </>
              ) : (
                <>
                  <div className="form-group">
                    <label>Sender Name</label>
                    <input className="input" value={senderName} onChange={(e) => setSenderName(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>Sender Email <span className="required">*</span></label>
                    <input className="input" value={senderEmail} onChange={(e) => setSenderEmail(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>SMTP Server <span className="required">*</span></label>
                    <input className="input" value={smtpServer} onChange={(e) => setSmtpServer(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>SMTP Port <span className="required">*</span></label>
                    <input className="input" value={smtpPort} onChange={(e) => setSmtpPort(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>IMAP Server</label>
                    <input className="input" value={imapServer} onChange={(e) => setImapServer(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>IMAP Port</label>
                    <input className="input" value={imapPort} onChange={(e) => setImapPort(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>Username <span className="required">*</span></label>
                    <input className="input" value={username} onChange={(e) => setUsername(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>Password <span className="required">*</span></label>
                    <input className="input" type="password" value={webmailPassword} onChange={(e) => setWebmailPassword(e.target.value)} />
                  </div>
                </>
              )}
            </div>
            
            {/* Custom Fields from Doctype */}
            {doctypeFields.filter(f => !['sec_general', 'email_type', 'sec_gmail', 'email', 'password', 'from_email', 'sec_webmail', 'sender_name', 'sender_email', 'smtp_server', 'smtp_port', 'imap_server', 'imap_port', 'username', 'webmail_password'].includes(f.id)).length > 0 && (
              <>
                <div className="header-divider" style={{ margin: '30px 0' }}></div>
                <div className="form-grid">
                  {doctypeFields.filter(f => !['sec_general', 'email_type', 'sec_gmail', 'email', 'password', 'from_email', 'sec_webmail', 'sender_name', 'sender_email', 'smtp_server', 'smtp_port', 'imap_server', 'imap_port', 'username', 'webmail_password'].includes(f.id)).map((field, idx) => (
                     <div className="form-group" key={idx}>
                       <label>{field.label}</label>
                       <input className="input" placeholder={field.label} />
                     </div>
                  ))}
                </div>
              </>
            )}

          </div>

          {/* Assignment UI Moved to Modal */}

          <div className="form-section-card theme-orange">
             <div className="section-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                  <div className="section-icon"><FaList /></div>
                  <h2>Saved Email Configurations</h2>
                </div>
             </div>
             <div style={{ maxHeight: '68vh', overflowY: 'auto', borderRadius: '10px', boxShadow: '0 5px 25px rgba(0, 0, 0, 0.03)', width: '100%', backgroundColor: '#ffffff', border: 'none' }} className="premium-card">
                <table className="premium-table">
                  <thead>
                    <tr>
                      <th>Context / Branches</th>
                      <th>Type</th>
                      <th>Email/User</th>
                      <th style={{ textAlign: 'right', paddingRight: '36px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const expandedList = [];
                      savedSettingsList.forEach(item => {
                        const compRaw = item.company_name || item.company;
                        const companies = compRaw ? compRaw.split(',').map(c => c.trim()) : ['Default Company'];
                        let branches = [null];
                        if (item.branch_names && Array.isArray(item.branch_names) && item.branch_names.length > 0) {
                          branches = item.branch_names;
                        } else if (typeof item.branch_names === 'string' && item.branch_names.trim()) {
                          branches = item.branch_names.split(',').map(b => b.trim());
                        }
                        companies.forEach(comp => {
                          branches.forEach(branch => {
                            expandedList.push({
                              ...item,
                              displayCompany: comp,
                              displayBranch: branch,
                              isDefault: !branch || branch === "Company-wide (Default)"
                            });
                          });
                        });
                      });

                      return expandedList.map((item, idx) => (
                        <tr key={idx}>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <div style={{ padding: '4px 10px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px', fontWeight: '600', color: '#475569' }}>
                                {item.displayCompany}
                              </div>
                              <span style={{ color: '#cbd5e1' }}>→</span>
                              <span style={{ padding: '4px 10px', borderRadius: '8px', fontSize: '12px', background: item.isDefault ? '#fef3c7' : '#e0f2fe', color: item.isDefault ? '#92400e' : '#0369a1', fontWeight: 'bold' }}>
                                {item.isDefault ? 'Company Default' : item.displayBranch}
                              </span>
                            </div>
                          </td>
                          <td style={{ textTransform: 'capitalize', color: '#334155', fontWeight: '600' }}>{item.email_type}</td>
                          <td style={{ color: '#334155', fontWeight: '600' }}>{item.email || item.username || item.sender_email}</td>
                          <td style={{ textAlign: 'right', paddingRight: '36px' }}>
                            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                               <button onClick={() => handleEdit(item)} title="Edit Configuration" className="action-btn action-btn-edit">
                                 <FaEdit style={{ fontSize: '20px' }} />
                               </button>
                               <button onClick={() => handleDeleteTarget(item, item.displayBranch, item.displayCompany)} title="Delete Configuration" className="action-btn action-btn-delete">
                                 <FaTrash style={{ fontSize: '20px' }} />
                               </button>
                            </div>
                          </td>
                        </tr>
                      ));
                    })()}
                    {savedSettingsList.length === 0 && (
                      <tr>
                        <td colSpan="4" style={{ padding: '40px', textAlign: 'center', color: '#94a3b8', fontWeight: '600' }}>No saved configurations found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
             </div>
          </div>
        </div>
      </div>

      {showAssignmentModal && (
        <div className="add-modal-overlay">
          <div className="add-modal" style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <span style={{ color: '#000000', fontWeight: 'bold' }}>Email Settings Assignments</span>
              <button className="close-modal-btn" onClick={() => setShowAssignmentModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '24px', color: '#ef4444' }}>&times;</button>
            </div>
            <div className="modal-body" style={{ marginTop: '20px' }}>
              {isGroupAdmin && (
                <div className="assignment-card" style={{ marginBottom: '20px' }}>
                  <h4 style={{ color: '#6366f1', display: 'flex', alignItems: 'center', gap: '8px' }}><FaUserLock /> Select Target Companies *</h4>
                  <div className="checkbox-group" style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
                    {assignedCompanies.map((comp, idx) => (
                      <label key={idx} className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 18px', background: '#eff2f9', borderRadius: '12px', border: '1.5px solid #C3CDE4', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
                        <input type="checkbox" checked={selectedCompanies.includes(comp)} onChange={(e) => {
                          let newComps;
                          if (e.target.checked) newComps = [...selectedCompanies, comp];
                          else newComps = selectedCompanies.filter(c => c !== comp);
                          setSelectedCompanies(newComps);
                          if (newComps.length > 0) fetchBranches(baseUrl, newComps.join(','));
                          else { setBranches([]); setSelectedBranches([]); }
                        }} style={{ width: '18px', height: '18px', accentColor: '#604BE8' }} />
                        {comp}
                      </label>
                    ))}
                  </div>
                </div>
              )}
              {branches.length > 0 && isCompanyAdmin && (!isGroupAdmin || (isGroupAdmin && selectedCompanies.length > 0)) && (
                <div className="assignment-card">
                  <h4 style={{ color: '#06D6A0', display: 'flex', alignItems: 'center', gap: '8px' }}><FaCog /> Select Branches</h4>
                  <div className="checkbox-group" style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
                    {branches.map((branch, idx) => {
                      const branchName = branch.branch_name || branch;
                      return (
                        <label key={idx} className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 18px', background: '#ecfdf5', borderRadius: '12px', border: '1.5px solid #d1fae5', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
                          <input type="checkbox" checked={selectedBranches.includes(branchName)} onChange={(e) => {
                            if (e.target.checked) setSelectedBranches([...selectedBranches, branchName]);
                            else setSelectedBranches(selectedBranches.filter(b => b !== branchName));
                          }} style={{ width: '18px', height: '18px', accentColor: '#06D6A0' }} />
                          {branchName}
                        </label>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="save-btn" onClick={() => {
                setShowAssignmentModal(false);
                handleSaveSettings();
              }} disabled={loading || (isGroupAdmin && selectedCompanies.length === 0)}>
                {loading ? "Saving..." : "Confirm & Save"}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {showDeleteConfirm && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, 
          background: 'rgba(0, 0, 0, 0.5)', zIndex: 9999, display: 'flex', 
          justifyContent: 'center', alignItems: 'center', backdropFilter: 'blur(5px)'
        }}>
          <div style={{
            background: '#ffffff', width: '90%', maxWidth: '400px', borderRadius: '24px', 
            padding: '40px 30px', boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
            textAlign: 'center', animation: 'fadeInScale 0.3s ease-out', color: '#1B1B29'
          }}>
            <div style={{ width: '70px', height: '70px', background: '#fee2e2', color: '#FF595E', borderRadius: '18px', display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '28px', margin: '0 auto 20px' }}>
              <FaTrash />
            </div>
            <h3 style={{ color: '#1B1B29', fontSize: '22px', fontWeight: '700', marginBottom: '10px' }}>Remove Configuration?</h3>
            <p style={{ color: '#777777', fontSize: '15px', marginBottom: '30px', lineHeight: '1.6', fontWeight: '500' }}>
              Are you sure you want to delete this configuration? This action is permanent and cannot be reversed.
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => setShowDeleteConfirm(false)} style={{ flex: 1, padding: '14px', borderRadius: '10px', border: '1px solid #E2E6EF', background: '#ffffff', color: '#1B1B29', fontWeight: '600', fontSize: '14px', cursor: 'pointer' }}>
                Keep Configuration
              </button>
              <button onClick={confirmDelete} style={{ flex: 1, padding: '14px', borderRadius: '10px', border: 'none', background: '#FF595E', color: '#ffffff', fontWeight: '600', fontSize: '14px', cursor: 'pointer', boxShadow: '0 4px 15px rgba(255, 89, 94, 0.3)' }}>
                Delete Forever
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EmailSettings;
